'use client';

import { useRef } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@ps/ui/atoms/dropdown-menu';
import { FileHeart, ImagePlus, Plus } from 'lucide-react';

/**
 * Composer `+` button. Opens a small dropdown with two attachment
 * actions: `Attach Images/Videos` (triggers a hidden `<input type="file">`)
 * and `Attach a Post` (delegates to the parent composer which owns
 * the picker modal — when omitted the action stays disabled). The
 * component owns the hidden file input so the trigger + picker live
 * together as one unit; the caller only sees `onFilesSelected(files)`.
 */
export function ChannelComposerAttachMenu({
  onFilesSelected,
  onAttachPost,
  disabled,
}: {
  onFilesSelected: (files: FileList) => void;
  onAttachPost?: () => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const openFilePicker = () => {
    if (inputRef.current) {
      // Clear the value so re-picking the same file fires `change`.
      inputRef.current.value = '';
      inputRef.current.click();
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Add attachment"
            disabled={disabled}
            className="hover:text-content-1 text-content-3 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus className="h-5 w-5" aria-hidden />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          side="top"
          className="text-content-1 border-line bg-surface-1 min-w-[200px]"
        >
          <DropdownMenuItem
            onSelect={openFilePicker}
            className="focus:text-content-1 focus:bg-surface-2"
          >
            <ImagePlus className="mr-2 h-4 w-4" aria-hidden />
            Attach Images/Videos
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={onAttachPost == null}
            onSelect={onAttachPost}
            className="focus:text-content-1 focus:bg-surface-2"
          >
            <FileHeart className="mr-2 h-4 w-4" aria-hidden />
            Attach a Post
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,video/mp4,video/quicktime"
        multiple
        className="hidden"
        onChange={event => {
          const files = event.target.files;
          if (files && files.length > 0) onFilesSelected(files);
        }}
      />
    </>
  );
}
